/**
 * Two-way bridge between a 5-field cron string and a small set of GRAPHICAL
 * scheduling modes (the `CronEditor` UI). The owner's rule: "the user must set
 * schedules graphically — days, hours, minutes" — so we model the COMMON shapes
 * a non-technical operator wants and fall back to a raw-cron `custom` mode for
 * anything exotic. This is the inverse of `cronText.describeCron` (which renders
 * a cron as a phrase); together they round-trip a schedule with no surprises.
 *
 * Pure functions only — `parseCron` (cron → spec) + `buildCron` (spec → cron) —
 * unit-tested. The React component reads/writes through them.
 */

export type CronMode =
  | "minutes" // every N minutes
  | "hourly" // every hour at minute M
  | "daily" // every day at HH:MM
  | "weekdays" // Mon–Fri at HH:MM
  | "weekly" // selected weekdays at HH:MM
  | "monthly" // day D of every month at HH:MM
  | "custom"; // raw cron (escape hatch)

export interface CronSpec {
  mode: CronMode;
  /** every N minutes (minutes mode) */
  everyMinutes: number;
  /** minute 0–59 (hourly mode) */
  minute: number;
  /** clock time (daily / weekdays / weekly / monthly) */
  hour: number;
  /** minute-of-hour for the clock time */
  atMinute: number;
  /** selected weekdays 0=Sun…6=Sat (weekly mode) */
  weekdays: number[];
  /** day of month 1–31 (monthly mode) */
  dayOfMonth: number;
  /** verbatim cron (custom mode) */
  raw: string;
}

/** Sensible blank slate — "every day at 02:00" is the canonical ETL default. */
export const DEFAULT_CRON_SPEC: CronSpec = {
  mode: "daily",
  everyMinutes: 15,
  minute: 0,
  hour: 2,
  atMinute: 0,
  weekdays: [1],
  dayOfMonth: 1,
  raw: "",
};

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

/** Build the 5-field cron string from a spec. `custom` returns the raw verbatim. */
export function buildCron(spec: CronSpec): string {
  const m = clamp(spec.atMinute, 0, 59);
  const h = clamp(spec.hour, 0, 23);
  switch (spec.mode) {
    case "minutes": {
      const n = clamp(spec.everyMinutes, 1, 59);
      return `*/${n} * * * *`;
    }
    case "hourly":
      return `${clamp(spec.minute, 0, 59)} * * * *`;
    case "daily":
      return `${m} ${h} * * *`;
    case "weekdays":
      return `${m} ${h} * * 1-5`;
    case "weekly": {
      const days = [...new Set(spec.weekdays)].sort((a, b) => a - b);
      const dow = days.length ? days.join(",") : "1";
      return `${m} ${h} * * ${dow}`;
    }
    case "monthly":
      return `${m} ${h} ${clamp(spec.dayOfMonth, 1, 31)} * *`;
    case "custom":
      return spec.raw.trim();
  }
}

const isInt = (s: string) => /^\d+$/.test(s);

/** Parse a cron string into a spec, choosing the most specific matching mode.
 * Anything that doesn't fit the graphical shapes lands in `custom` (raw kept). */
export function parseCron(cron: string | null | undefined): CronSpec {
  const base = { ...DEFAULT_CRON_SPEC };
  const raw = (cron ?? "").trim();
  if (!raw) return base;
  base.raw = raw;
  const parts = raw.split(/\s+/);
  if (parts.length !== 5) return { ...base, mode: "custom" };
  const [min, hour, dom, mon, dow] = parts;

  // every N minutes — */N * * * *
  const everyN = /^\*\/(\d+)$/.exec(min);
  if (everyN && hour === "*" && dom === "*" && mon === "*" && dow === "*") {
    return { ...base, mode: "minutes", everyMinutes: clamp(Number(everyN[1]), 1, 59) };
  }
  // hourly — M * * * *
  if (isInt(min) && hour === "*" && dom === "*" && mon === "*" && dow === "*") {
    return { ...base, mode: "hourly", minute: clamp(Number(min), 0, 59) };
  }
  // the remaining shapes need a concrete minute + hour
  if (isInt(min) && isInt(hour)) {
    const m = clamp(Number(min), 0, 59);
    const h = clamp(Number(hour), 0, 23);
    // daily — M H * * *
    if (dom === "*" && mon === "*" && dow === "*") {
      return { ...base, mode: "daily", hour: h, atMinute: m };
    }
    // weekdays — M H * * 1-5
    if (dom === "*" && mon === "*" && dow === "1-5") {
      return { ...base, mode: "weekdays", hour: h, atMinute: m };
    }
    // weekly (list of single weekdays) — M H * * 1,3,5  (or a single 0–6)
    if (dom === "*" && mon === "*" && /^[0-6](,[0-6])*$/.test(dow)) {
      const weekdays = [...new Set(dow.split(",").map(Number))].sort((a, b) => a - b);
      return { ...base, mode: "weekly", hour: h, atMinute: m, weekdays };
    }
    // monthly — M H D * *
    if (isInt(dom) && mon === "*" && dow === "*") {
      return { ...base, mode: "monthly", hour: h, atMinute: m, dayOfMonth: clamp(Number(dom), 1, 31) };
    }
  }
  return { ...base, mode: "custom" };
}
